import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator"; // Gunakan separator
import api from "@/lib/axios";
import {
  getListCapaianPrestasi,
  getListGrupPrestasi,
  getListJenisPrestasi,
  getListJenjangPrestasi,
} from "@/services/getData";
import {
  CapaianPrestasi,
  DataPrestasi,
  GrupPrestasi,
  JenisPrestasi,
  JenjangPrestasi,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Award, FileText, Loader2, Trash2, UploadCloud } from "lucide-react"; // Gunakan Lucide icons
import { useState } from "react";
import toast from "react-hot-toast";

const postData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/prestasi/store",
    newData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

const patchData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/prestasi/update",
    newData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};

const deleteData = async (deletedData: FormData) => {
  const id_data_prestasi = deletedData.get("id_data_prestasi");
  const response = await api({
    url: "/siswa/penerimaan/smbjm/prestasi/delete",
    method: "DELETE",
    data: { id_data_prestasi },
  });
  return response.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: DataPrestasi | null;
  closeModal: () => void;
  idDataPendaftaran?: string;
}) {
  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  // --- QUERIES ---
  const { data: dataJenisPrestasi, isPending: isPendingDataJenisPrestasi } =
    useQuery({
      queryKey: ["listJenisPrestasi"],
      queryFn: getListJenisPrestasi,
      select: (data) =>
        data?.map((item: JenisPrestasi) => ({
          label: item.nama_jenis_prestasi,
          value: `${item.id_ref_jenis_prestasi}`,
        })),
    });
  const { data: dataJenjangPrestasi, isPending: isPendingDataJenjangPrestasi } =
    useQuery({
      queryKey: ["listJenjangPrestasi"],
      queryFn: getListJenjangPrestasi,
      select: (data) =>
        data?.map((item: JenjangPrestasi) => ({
          label: item.nama_jenjang_prestasi,
          value: `${item.id_ref_jenjang_prestasi}`,
        })),
    });
  const { data: dataGrupPrestasi, isPending: isPendingDataGrupPrestasi } =
    useQuery({
      queryKey: ["listGrupPrestasi"],
      queryFn: getListGrupPrestasi,
      select: (data) =>
        data?.map((item: GrupPrestasi) => ({
          label: item.nama_grup_prestasi,
          value: `${item.id_ref_grup_prestasi}`,
        })),
    });
  const { data: dataCapaianPrestasi, isPending: isPendingDataCapaianPrestasi } =
    useQuery({
      queryKey: ["listCapaianPrestasi"],
      queryFn: getListCapaianPrestasi,
      select: (data) =>
        data?.map((item: CapaianPrestasi) => ({
          label: item.nama_capaian_prestasi,
          value: `${item.id_ref_capaian_prestasi}`,
        })),
    });

  // --- STATE ---
  const [form, setForm] = useState<DataPrestasi>({
    id_data_prestasi: selectedRow?.id_data_prestasi || 0,
    id_data_pendaftaran: Number(idDataPendaftaran) || 0,
    id_ref_jenis_prestasi: selectedRow?.id_ref_jenis_prestasi || "",
    id_ref_jenjang_prestasi: selectedRow?.id_ref_jenjang_prestasi || "",
    id_ref_grup_prestasi: selectedRow?.id_ref_grup_prestasi || "",
    id_ref_capaian_prestasi: selectedRow?.id_ref_capaian_prestasi || "",
    urutan_prestasi: selectedRow?.urutan_prestasi || "",
    lembaga_pemberi_prestasi: selectedRow?.lembaga_pemberi_prestasi || "",
    tahun_prestasi: selectedRow?.tahun_prestasi || "",
    deskripsi: selectedRow?.deskripsi || "",
  });
  const [file, setFile] = useState<File | null>(null);

  // Combobox Open States
  const [openJenisPrestasi, setOpenJenisPrestasi] = useState(false);
  const [openJenjangPrestasi, setOpenJenjangPrestasi] = useState(false);
  const [openGrupPrestasi, setOpenGrupPrestasi] = useState(false);
  const [openCapaianPrestasi, setOpenCapaianPrestasi] = useState(false);

  // --- MUTATION ---
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listPrestasi"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      closeModal();
      toast.success(data?.message || "Berhasil.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      toast.error(err.response?.data?.message || "Terjadi kesalahan.");
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) setFile(file);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData();

    if (!isPatch || isDelete) {
      formData.append(
        "id_data_prestasi",
        String(selectedRow?.id_data_prestasi),
      );
    }
    if (file) formData.append("file", file);

    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    mutate(formData);
  };

  // --- DELETE UI ---
  if (isDelete) {
    return (
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="p-3 bg-red-100 rounded-full text-red-600 dark:bg-red-900/30">
            <Trash2 className="w-8 h-8" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Hapus Data Prestasi?
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Anda yakin ingin menghapus data prestasi <br />
              <span className="font-medium text-slate-700 dark:text-slate-300">
                "{selectedRow?.jenis_prestasi?.nama_jenis_prestasi}"
              </span>
              ?
              <br />
              Tindakan ini tidak dapat dibatalkan.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={closeModal}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            disabled={isPending}
            type="submit"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              "Ya, Hapus"
            )}
          </Button>
        </div>
      </form>
    );
  }

  // --- INPUT/EDIT UI ---
  return (
    <form onSubmit={handleSubmit} className="p-6">
      <FormHeader
        icon={<Award className="text-blue-600" />}
        title={isPost ? "Tambah Prestasi Baru" : "Edit Data Prestasi"}
        description="Lengkapi detail prestasi yang Anda miliki."
      />

      <Separator className="my-6" />

      <div className="grid gap-6">
        {/* ROW 1: JENIS & JENJANG */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label required>Jenis Prestasi</Label>
            <Combobox
              isPending={isPendingDataJenisPrestasi}
              open={openJenisPrestasi}
              setOpen={setOpenJenisPrestasi}
              value={
                form.id_ref_jenis_prestasi === 0
                  ? ""
                  : String(form.id_ref_jenis_prestasi)
              }
              setValue={(val) =>
                setForm((prev) => ({
                  ...prev,
                  id_ref_jenis_prestasi: Number(val),
                }))
              }
              options={dataJenisPrestasi}
              placeholder="Pilih jenis..."
            />
            <InputError
              text={error?.response?.data?.errors?.id_ref_jenis_prestasi?.join(
                ",",
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Jenjang Prestasi</Label>
            <Combobox
              isPending={isPendingDataJenjangPrestasi}
              open={openJenjangPrestasi}
              setOpen={setOpenJenjangPrestasi}
              value={
                form.id_ref_jenjang_prestasi === 0
                  ? ""
                  : String(form.id_ref_jenjang_prestasi)
              }
              setValue={(val) =>
                setForm((prev) => ({
                  ...prev,
                  id_ref_jenjang_prestasi: Number(val),
                }))
              }
              options={dataJenjangPrestasi}
              placeholder="Pilih jenjang..."
            />
            <InputError
              text={error?.response?.data?.errors?.id_ref_jenjang_prestasi?.join(
                ",",
              )}
            />
          </div>
        </div>

        {/* ROW 2: GRUP & CAPAIAN */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label required>Grup Prestasi</Label>
            <Combobox
              isPending={isPendingDataGrupPrestasi}
              open={openGrupPrestasi}
              setOpen={setOpenGrupPrestasi}
              value={
                form.id_ref_grup_prestasi === 0
                  ? ""
                  : String(form.id_ref_grup_prestasi)
              }
              setValue={(val) =>
                setForm((prev) => ({
                  ...prev,
                  id_ref_grup_prestasi: Number(val),
                }))
              }
              options={dataGrupPrestasi}
              placeholder="Pilih grup..."
            />
            <InputError
              text={error?.response?.data?.errors?.id_ref_grup_prestasi?.join(
                ",",
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Capaian Prestasi</Label>
            <Combobox
              isPending={isPendingDataCapaianPrestasi}
              open={openCapaianPrestasi}
              setOpen={setOpenCapaianPrestasi}
              value={
                form.id_ref_capaian_prestasi === 0
                  ? ""
                  : String(form.id_ref_capaian_prestasi)
              }
              setValue={(val) =>
                setForm((prev) => ({
                  ...prev,
                  id_ref_capaian_prestasi: Number(val),
                }))
              }
              options={dataCapaianPrestasi}
              placeholder="Pilih capaian..."
            />
            <InputError
              text={error?.response?.data?.errors?.id_ref_capaian_prestasi?.join(
                ",",
              )}
            />
          </div>
        </div>

        {/* ROW 3: LEMBAGA & TAHUN */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label required>Lembaga Penyelenggara</Label>
            <Input
              placeholder="Contoh: Kemendikbud, KONI"
              value={form.lembaga_pemberi_prestasi || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  lembaga_pemberi_prestasi: e.target.value,
                }))
              }
            />
            <InputError
              text={error?.response?.data?.errors?.lembaga_pemberi_prestasi?.join(
                ",",
              )}
            />
          </div>

          <div className="space-y-2">
            <Label required>Tahun Perolehan</Label>
            <Input
              minLength={4}
              maxLength={4}
              placeholder="YYYY"
              value={form.tahun_prestasi || ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  tahun_prestasi: e.target.value.replace(/\D/g, ""),
                }))
              }
            />
            <InputError
              text={error?.response?.data?.errors?.tahun_prestasi?.join(",")}
            />
          </div>
        </div>

        {/* ROW 4: FILE UPLOAD */}
        <div className="space-y-3 pt-2">
          <Label required className="text-slate-900 dark:text-slate-100">
            Upload Bukti Prestasi (PDF/Gambar)
          </Label>
          <div
            className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              file
                ? "border-blue-400 bg-blue-50/50 dark:border-blue-500/50 dark:bg-blue-900/20"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-900/50"
            }`}
          >
            <Input
              type="file"
              accept=".pdf,image/*"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="flex flex-col items-center gap-2 pointer-events-none">
              {file ? (
                <>
                  <FileText className="w-8 h-8 text-blue-500 dark:text-blue-400" />
                  <div className="text-sm font-medium text-blue-700 dark:text-blue-300 line-clamp-1 px-4">
                    {file.name}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {(file.size / 1024).toFixed(0)} KB
                  </div>
                </>
              ) : (
                <>
                  <UploadCloud className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      Klik untuk upload
                    </span>{" "}
                    atau drag and drop
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    Maksimal ukuran file 1 MB
                  </div>
                </>
              )}
            </div>
          </div>
          <InputError text={error?.response?.data?.errors?.file?.join(",")} />
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={closeModal}
          disabled={isPending}
          className="flex-1"
        >
          Batal
        </Button>
        <Button className="flex-1" disabled={isPending} type="submit">
          {isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            "Simpan Data"
          )}
        </Button>
      </div>
    </form>
  );
}
